import { Badge, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui";
import { cn, ROLE_BOM, stateLawStatus, toApiSort } from "@/lib/utils";
import { RootState } from "@/redux/store";
import { routes } from "@/routes/utils";
import { useGetContactStateTemplateList } from "@/services";
import { Bell } from "lucide-react";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

const useStateLevelTnc = () => {
  const navigate = useNavigate();
  const [globalFilter, setGlobalFilter] = useState("");
  const [pageNumber, setPageNumber] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const [sorting, setSorting] = useState([{ id: "createdAt", desc: true }]);
  const [currentTab, setCurrentTab] = useState<string>("all");
  const communityDetail = useSelector(
    (state: RootState) => state.currentData.communityData
  );

  const breadcrumbData = useMemo(
    () => [
      {
        link: `/${routes.admin}/${routes.masterData}/${routes.templates}`,
        name: `Templates - ${communityDetail?.name || ""}`,
      },
      {
        link: "",
        name: <span className="text-primary">State Level TnC Template</span>,
      },
    ],
    []
  );
  
  const masterDataPermission = useSelector((state: RootState) => state.currentData.masterDataPermission);
  const currentRole = useSelector((state: RootState) => state.currentData.currentRole);
  const isCamBom = location.pathname.startsWith("/cam") || location.pathname.startsWith("/bom") || (location.pathname.startsWith("/owner") && currentRole == ROLE_BOM);

  const hasAddPermission = useMemo(() => isCamBom ? masterDataPermission?.add : true, [masterDataPermission]);
  // const hasDeletePermission = useMemo(() => isCamBom ? masterDataPermission?.delete : true, [masterDataPermission]);
  // const hasUpdatePermission = useMemo(() => isCamBom ? masterDataPermission?.update : true, [masterDataPermission]);
  
  const onBackClick = () => {
    navigate(`/${routes.admin}/${routes.masterData}/${routes.templates}`);
  };

  const onAddTemplate = () => {
    navigate(`${routes.add}`);
  };

  const {data: contractList, isLoading: isContractLoading, refetch: refetchContractList, isRefetching: isRefetchingContract} = useGetContactStateTemplateList({
    data: {
      pageNumber, 
      pageSize,
      ...(currentTab != "all" ? {filters: [{
        "field": "status",
        "value": currentTab,
        "operator": "EQUAL"
      }]} : {}),
      sort: toApiSort(sorting, { field: "createdAt", direction: "DESC" }),
    }
  })

  const onCurrentTabChange = (tab: any) => {
    setCurrentTab(tab);
    setTimeout(() => refetchContractList(), 100);
  };

  useEffect(() => {
    refetchContractList();
  }, [pageNumber, pageSize, sorting]);

  useEffect(() => {
    if (globalFilter?.length >= 2 || globalFilter?.length == 0) {
      refetchContractList();
    }
  }, [globalFilter]);

  // Define Table Columns
  const columns = [
    {
      accessorKey: "stateTemplateId",
      header: "ID",
      accessorFn: (row: any) => String(row.stateTemplateId || ""),
    },
    {
      accessorKey: "versionNo",
      header: "Version No",
      accessorFn: (row: any) => String(row.versionNo || ""),
    },
    {
      accessorKey: "licenseCategory",
      header: "License Category",
      accessorFn: (row: any) => String(row.licenseCategory || ""),
    },
    {
      accessorKey: "templateName",
      header: "Template Name",
      accessorFn: (row: any) => String(row.templateName || ""),
    },
    {
      accessorKey: "stateName",
      header: "State",
      accessorFn: (row: any) => String(row.stateName || ""),
    },
    {
      accessorKey: "createdAt",
      header: "Created On",
      accessorFn: (row: any) =>
        row.createdAt ? moment(row.createdAt).format("MM/DD/YYYY") : "",
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: (row: any) => {
        return (
          <Badge
            variant="outline"
            className={cn(
              (stateLawStatus as any)[row.row.original.status?.toLowerCase()]
                ?.color,
              "rounded-full px-4 py-2"
            )}
          >
            {
              (stateLawStatus as any)[row.row.original.status?.toLowerCase()]
                ?.label
            }
          </Badge>
        );
      },
    },
    {
      accessorKey: "isGlobalTemplateChanged",
      header: "",
      cell: ({row}: any) => {
        return row?.original?.isGlobalTemplateChanged || row?.original?.isStateLawBlockChanged ? (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger className="mx-1" type="button">
                <Bell className="h-5 w-5 stroke-primary" />
              </TooltipTrigger>
              <TooltipContent>
                {
                  row?.original?.isGlobalTemplateChanged ?
                  <p>New COMMUNACAMS rules went into effect on <b>{row?.original?.globalTemplateUpdatedAt ? moment(row?.original?.globalTemplateUpdatedAt).format('MM/DD/YYYY') : ''}</b>. These <br/>
                  are not currently covered in the published T&C document. Please <br/>
                  upload a new T&C version to ensure we remain compliant <br/>
                  with the latest regulations.</p> : <></>
                }
                <br/>
                {row?.original?.isStateLawBlockChanged ? 
                  <p>New state laws for <b>{row?.original?.stateName || ''}</b> went into effect on <b>{row?.original?.stateLawUpdatedAt ? moment(row?.original?.stateLawUpdatedAt).format('MM/DD/YYYY') : ''}</b>. These <br/> 
                    are not currently covered in the published T&C document. Please <br/>upload 
                    a new T&C version to ensure we remain compliant <br/>with the latest state regulations.</p>
                  : <></> }
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : <></>
      }
    },
  ];

  const onRowClick = (row: any) => {
    navigate(`${row.original.stateTemplateId}/view`);
  }

    return {
      communityDetail,
      breadcrumbData,
      onBackClick,
      globalFilter,
      setGlobalFilter,
      pageNumber,
      setPageNumber,
      pageSize,
      setPageSize,
      sorting,
      setSorting,
      onAddTemplate,
      onCurrentTabChange,
      currentTab,
      isContractLoading,
      isRefetchingContract,
      columns,
      contractList,
      onRowClick,
      hasAddPermission
    };
};

export default useStateLevelTnc;
